import React from 'react';
import { useNavigate } from 'react-router-dom';
import HeroSection from '../components/HeroSection';
import ServiceCard from '../components/ServiceCard';
import CTABanner from '../components/CTABanner';
import './HomePage.css';

function HomePage() {
  const navigate = useNavigate();

  return (
    <div className="home-page">
      <HeroSection
        backgroundImage="/images/hero_homepage.jpg"
        title="Commercial Carpet Cleaning Across Oxfordshire"
        subtitle="Deep-cleaned carpets that impress clients, improve hygiene, and extend carpet life — delivered around your schedule with zero disruption."
        ctaText="Get a Free Quote"
        ctaLink="/contact"
      />

      {/* Trust Bar */}
      <div className="section--soft home-trust">
        <div className="container">
          <div className="trust-badges">
            <div className="trust-badge">
              <span className="trust-badge__icon">&#10003;</span>
              <span>Fully Insured</span>
            </div>
            <div className="trust-badge">
              <span className="trust-badge__icon">&#10003;</span>
              <span>Eco-Friendly Products</span>
            </div>
            <div className="trust-badge">
              <span className="trust-badge__icon">&#10003;</span>
              <span>Evening &amp; Weekend Service</span>
            </div>
          </div>
        </div>
      </div>

      {/* Services Overview */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <h2>Specialist Cleaning for Every Commercial Environment</h2>
            <p>
              From five-star hotels to corporate offices and family homes, we tailor our 
              approach to your space, your schedule, and your standards.
            </p>
          </div>
          <div className="grid grid--3">
            <ServiceCard
              icon="&#127976;"
              title="Hotels &amp; Hospitality"
              description="Discreet, overnight cleaning for guest rooms, lobbies, and function spaces — scheduled around your occupancy."
              linkTo="/hotels"
              linkLabel="Hotel cleaning"
            />
            <ServiceCard
              icon="&#127970;"
              title="Offices &amp; Workspaces"
              description="After-hours deep cleaning that keeps your workplace hygienic, professional, and impressive to every visitor."
              linkTo="/offices"
              linkLabel="Office cleaning"
            />
            <ServiceCard
              icon="&#127968;"
              title="Home Cleaning"
              description="Professional carpet cleaning for your home — living rooms, bedrooms, stairs, and hallways restored to their best."
              linkTo="/domestic"
              linkLabel="Home cleaning"
            />
          </div>
        </div>
      </section>

      {/* Full-width Image Break */}
      <div
        className="image-break"
        style={{ backgroundImage: 'url(/images/home_commercial_showcase.jpg)' }}
      >
        <div className="image-break__overlay" />
        <div className="image-break__content">
          <h2>Results That Speak for Themselves</h2>
          <p>
            We use professional-grade hot-water extraction equipment and eco-friendly 
            products to deliver a deep clean that transforms carpets and extends their lifespan.
          </p>
        </div>
      </div>

      {/* How It Works */}
      <section className="section">
        <div className="container">
          <div className="home-how">
            <div className="home-how__steps">
              <h2>How It Works</h2>
              <p className="home-how__intro">
                From first call to finished clean, we keep it simple.
              </p>
              <div className="home-how__list">
                <div className="home-how__step">
                  <span className="home-how__num">1</span>
                  <div>
                    <strong>Get in touch</strong>
                    <p>Call us or fill in the form. We'll discuss your space and requirements.</p>
                  </div>
                </div>
                <div className="home-how__step">
                  <span className="home-how__num">2</span>
                  <div>
                    <strong>Free survey &amp; quote</strong>
                    <p>We assess your carpets and provide a clear, no-obligation quote.</p>
                  </div>
                </div>
                <div className="home-how__step">
                  <span className="home-how__num">3</span>
                  <div>
                    <strong>Professional clean</strong>
                    <p>Our team cleans at a time that suits you — evenings, nights, or weekends.</p>
                  </div>
                </div>
                <div className="home-how__step">
                  <span className="home-how__num">4</span>
                  <div>
                    <strong>Inspect &amp; enjoy</strong>
                    <p>We walk through the results together so you're completely satisfied.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="home-how__image">
              <img
                src="/images/carpet_deep_clean_action.jpg"
                alt="Professional carpet deep cleaning showing dramatic before-and-after results"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section section--soft">
        <div className="container">
          <div className="split">
            <div className="split__image">
              <img
                src="/images/team_professional_portrait.jpg"
                alt="Our professional carpet cleaning team in Oxfordshire"
              />
            </div>
            <div className="split__content">
              <h2>Why Oxfordshire Businesses Choose Us</h2>
              <p>
                We're a family-run team with commercial expertise. Every client's space gets the 
                same care and attention — whether it's a single office or an entire hotel.
              </p>
              <ul className="home-why__list">
                <li>
                  <strong>Zero disruption</strong> — we work around your operating hours
                </li>
                <li>
                  <strong>Eco-friendly methods</strong> — safe for staff, guests, and the environment
                </li>
                <li>
                  <strong>Fully insured</strong> — public and employer's liability cover
                </li>
              </ul>
              <button
                className="btn btn--outline"
                onClick={() => navigate('/why-choose-us')}
              >
                Learn More About Us
              </button>
            </div>
          </div>
        </div>
      </section>

      <CTABanner
        heading="Let's Discuss Your Cleaning Requirements"
        description="Whether it's a one-off deep clean or an ongoing maintenance plan, we'll tailor a solution to your needs."
        ctaText="Request a Free Quote"
      />
    </div>
  );
}

export default HomePage;
